import axios from 'axios';

const STORAGE_KEY = 'academicYear';
const CURRENT_STORAGE_KEY = 'academicYearCurrent';
export const ACADEMIC_YEAR_POLL_INTERVAL_MS = 30000;
let currentYearMeta = null;

export function getUrlAcademicYear(pathname = window.location.pathname) {
  const match = String(pathname || '').match(/^\/(\d{4})(?=\/|$)/);
  return match ? match[1] : '';
}

export function getAcademicYearBasename() {
  const year = getUrlAcademicYear();
  return year ? `/${year}` : undefined;
}

export function getSelectedYear() {
  // Yıl içeren adreslerde URL tek doğruluk kaynağıdır. Böylece başka bir
  // tarayıcıda açılan/kopyalanan ders bağlantısı doğru yıl verisini getirir.
  return getUrlAcademicYear() || localStorage.getItem(STORAGE_KEY) || '';
}

export function setSelectedYear(year) {
  if (year) localStorage.setItem(STORAGE_KEY, year);
  else localStorage.removeItem(STORAGE_KEY);
  if (!year) {
    window.location.reload();
    return;
  }

  const currentPath = window.location.pathname || '/';
  const yearPrefix = /^\/\d{4}(?=\/|$)/;
  const nextPath = yearPrefix.test(currentPath)
    ? currentPath.replace(yearPrefix, `/${year}`)
    : `/${year}${currentPath === '/' ? '/' : currentPath}`;
  window.location.assign(`${nextPath}${window.location.search}${window.location.hash}`);
}

export function setYearMeta(meta) {
  currentYearMeta = meta;
  document.body.classList.toggle('academic-year-readonly', Boolean(meta && !meta.editable));
  window.dispatchEvent(new CustomEvent('academic-year-meta-changed', { detail: meta }));
}

export function reconcileAcademicYears(data) {
  if (!Array.isArray(data)) return null;

  const current = data.find((item) => item.is_current && item.status === 'active')
    || data.find((item) => item.is_current)
    || null;
  const previousCurrent = localStorage.getItem(CURRENT_STORAGE_KEY) || '';
  const urlYear = getUrlAcademicYear();
  // EBS'te URL yil seciminin kalici kaynagidir. Yilsiz bir adres eski
  // localStorage degerini devralmaz; dogrudan aktif yila yonlendirilir.
  const stored = urlYear;
  const currentChanged = Boolean(
    current?.year && previousCurrent && previousCurrent !== current.year
  );
  const selectedWasCurrent = !stored || stored === previousCurrent;
  const storedExists = data.some((item) => item.year === stored);
  const selected = currentChanged && selectedWasCurrent
    ? current.year
    : (storedExists ? stored : (current?.year || ''));

  if (current?.year) localStorage.setItem(CURRENT_STORAGE_KEY, current.year);
  if (selected) localStorage.setItem(STORAGE_KEY, selected);
  else localStorage.removeItem(STORAGE_KEY);

  const selectedMeta = data.find((item) => item.year === selected) || current;
  setYearMeta(selectedMeta || null);

  return {
    years: data,
    current,
    selected,
    currentChanged,
    selectionChanged: selected !== stored,
    urlNeedsUpdate: Boolean(selected && urlYear !== selected),
  };
}

export function startAcademicYearMonitor(loadYears, onUpdate, intervalMs = ACADEMIC_YEAR_POLL_INTERVAL_MS) {
  let stopped = false;
  let requestInFlight = false;

  const refresh = async () => {
    if (stopped || requestInFlight) return;
    requestInFlight = true;
    try {
      const data = await loadYears();
      const result = reconcileAcademicYears(data);
      if (!stopped && result) onUpdate(result);
    } catch {
      // Gecici ag hatalarinda mevcut yil secimi korunur; sonraki tur yeniden dener.
    } finally {
      requestInFlight = false;
    }
  };

  const refreshWhenVisible = () => {
    if (document.visibilityState === 'visible') refresh();
  };

  refresh();
  const timerId = window.setInterval(refresh, intervalMs);
  document.addEventListener('visibilitychange', refreshWhenVisible);
  window.addEventListener('focus', refresh);

  return () => {
    stopped = true;
    window.clearInterval(timerId);
    document.removeEventListener('visibilitychange', refreshWhenVisible);
    window.removeEventListener('focus', refresh);
  };
}

export function announceAcademicYearChange(result) {
  if (!result?.currentChanged || !result.current) return false;
  window.alert(
    `Akademik yıl ${result.current.label || result.current.year} olarak güncellendi.`
  );
  return result.selectionChanged;
}

export function isReadOnly() {
  return Boolean(currentYearMeta && !currentYearMeta.editable);
}

export function canUploadCoursePlan() {
  return Boolean(currentYearMeta && currentYearMeta.course_plan_uploadable);
}

export function isPlanningYear() {
  return Boolean(currentYearMeta && currentYearMeta.status === 'planning');
}

export function isNonMutatingAcademicYearRequest(input, init = {}) {
  try {
    const url = input instanceof Request ? input.url : input;
    const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    if (method !== 'POST') return false;
    const path = new URL(url, window.location.origin).pathname;
    return /^\/api\/program\/\d+\/program-ciktilari\/oneri$/.test(path)
      || /^\/api\/program\/\d+\/ders-plani\/oneri$/.test(path)
      || /^\/api\/program\/\d+\/ders-plani\/(?:rapor-onizleme|rapor|nihai-pdf)$/.test(path);
  } catch {
    return false;
  }
}

function isPlanningAllowedWrite(input, init = {}) {
  if (!currentYearMeta || currentYearMeta.status !== 'planning') return false;
  try {
    const url = input instanceof Request ? input.url : input;
    const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const path = new URL(url, window.location.origin).pathname;
    if (method === 'POST' && path === '/api/admin/courses/upload-excel') return true;
    if (method === 'POST' && /^\/api\/program\/\d+\/program-ciktilari\/oneri$/.test(path)) return true;
    return ['POST', 'PUT', 'DELETE'].includes(method)
      && /^\/api\/programs\/\d+\/program-outcomes(?:\/copy-previous|\/upload-word)?$/.test(path);
  } catch {
    return false;
  }
}

function isNonAcademicYearWrite(input) {
  try {
    const url = input instanceof Request ? input.url : input;
    const path = new URL(url, window.location.origin).pathname;
    return ['/api/unified-login', '/api/admin/login', '/api/program-admin/login'].includes(path);
  } catch {
    return false;
  }
}

export function getEbsToken() {
  for (const key of ['programAdminToken', 'universityAdminToken', 'adminToken']) {
    const token = sessionStorage.getItem(key) || localStorage.getItem(key);
    if (token) return token;
  }
  return '';
}

export function installAcademicYearHeader() {
  if (!window.__academicYearFetchPatched) {
    window.__academicYearFetchPatched = true;
    const originalFetch = window.fetch.bind(window);
    window.fetch = (input, init = {}) => {
      const method = (init.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
      if (isReadOnly() && !['GET', 'HEAD', 'OPTIONS'].includes(method)
          && !isNonMutatingAcademicYearRequest(input, init)
          && !isPlanningAllowedWrite(input, init)
          && !isNonAcademicYearWrite(input)) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      const year = getSelectedYear();
      if (!year) return originalFetch(input, init);
      const headers = new Headers(init.headers || (input instanceof Request ? input.headers : undefined));
      headers.set('X-Academic-Year', year);
      if (input instanceof Request) return originalFetch(new Request(input, { headers }));
      return originalFetch(input, { ...init, headers });
    };
  }

  if (!window.__academicYearAxiosPatched) {
    window.__academicYearAxiosPatched = true;
    axios.interceptors.request.use((config) => {
      const method = (config.method || 'get').toLowerCase();
      if (isReadOnly() && !['get', 'head', 'options'].includes(method)
          && !isNonMutatingAcademicYearRequest(config.url || '', { method })
          && !isPlanningAllowedWrite(config.url || '', { method })
          && !isNonAcademicYearWrite(config.url || '')) {
        return Promise.reject(new Error('Arşiv akademik yılında değişiklik yapılamaz.'));
      }
      const year = getSelectedYear();
      if (year) {
        config.headers = config.headers || {};
        config.headers['X-Academic-Year'] = year;
      }
      return config;
    });
  }
}
